import React, { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Spinner, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from 'react-select';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { useTranslation } from "react-i18next";
import { debounce } from 'lodash';
import './Actes.css';

// Actions
import {
  getActesAction,
  getActePagesAction,
  startLoader,
  createActeAction,
  importActeFileUploadAction,
  updateActeAction,
  getProvidersTypesAction,
  deleteDataAction,
  downloadActeTemplateAction
} from '../../../../store/actions/ProviderActions';
import { getGarantiesAction, getActesProdAction } from '../../../../store/actions/ContratActions';
import { searchActesAction } from '../../../../store/actions/ProviderActions';
import { currentUser } from "../../../../store/selectors/CurrentUserSelector";

const Acte = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Initial states
  const initialFormState = {
    nameFr: '',
    nameEn: '',
    code: '',
    coefLettre: 0,
    acteId: '',
    typeActeId: '',
    lettreCleId: '',
    garantieId: '',
    codeGarantie: '',
    garantieName: '',
    acteProdCode: '',
    acteProdName: '',
    providerTypeId: [],
    isExclu: false,
    isAccord: false,
    isAmbulatoire: true,
    isHospitalisation: true,
    isMultiple: false,
    type: ""
  };

  const initialErrorState = { name: '' };

  // States
  const [formData, setFormData] = useState(initialFormState);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState(initialErrorState);
  const [search, setSearch] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [addCard, setAddCard] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 0,
    sortField: null,
    sortOrder: null,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      familyText: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      garantieName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      acteProdName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    }
  });

  // Helper to update formData
  const updateFormField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Download template handler
  const handleDownloadTemplate = useCallback(() => {
    dispatch(downloadActeTemplateAction());
  }, [dispatch]);

  // Utility functions
  const initFilters = useCallback(() => {
    const baseFilters = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      familyText: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      garantieName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      acteProdName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    };
    setFilters(baseFilters);
    setGlobalFilterValue("");
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
    setFile(null);
    setErrors(initialErrorState);
    setUpdateMode(false);
  }, [initialFormState, initialErrorState]);

  const openCreateModal = useCallback(() => {
    resetForm();
    setAddCard(true);
  }, [resetForm]);

  const closeModal = useCallback(() => {
    resetForm();
    setAddCard(false);
  }, [resetForm]);

  const validateUniqueness = useCallback(() => {
    const existingActes = globalFilterValue.length > 0 ? props.actesRes : props.actesPages?.data || [];
    
    const codeExists = existingActes.some(acte => 
      acte.code.toLowerCase() === formData.code.toLowerCase() && 
      (!updateMode || acte.id !== formData.acteId)
    );
    
    const nameExists = existingActes.some(acte => 
      acte.nameFr.toLowerCase() === formData.nameFr.toLowerCase() && 
      (!updateMode || acte.id !== formData.acteId)
    );
    
    return { codeExists, nameExists };
  }, [formData.code, formData.nameFr, formData.acteId, updateMode, globalFilterValue, props.actesRes, props.actesPages]);

  // Act types
  const actTypes = useCallback(() => [
    { label: t("common.room"), value: "Chambre" },
    { label: t("common.exam"), value: "Examen" },
    { label: t("layout.specialized-acts"), value: "Acte spécialisé" },
    { label: t("common.travel-exp"), value: "Frais de déplacement" },
    { label: t('layout.optics'), value: "Optique" },
    { label: t("common.dental"), value: "Dentaire" },
    { label: "Consultation", value: "Consultation" },
    { label: t("common.acc-glasses"), value: "Accessoires verres" },
    { label: t('layout.pharmacy'), value: "Pharmacie" },
  ], [t]);

  // Permissions
  const canDo = useCallback((menuCode, actionCode) => {
    const theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
    return theMenu ? theMenu.actionMenus?.some(action => action.code === actionCode) : false;
  }, [props.currentUser.menus]);
  
  const hasMenu = useCallback((menuCode) => {
    return props.currentUser.menus?.some(menu => menu.code === menuCode) || false;
  }, [props.currentUser.menus]);

  // Debounced search
  const debouncedSearch = useCallback(
  debounce((searchValue) => {
    if (searchValue.trim().length > 0) {
      dispatch(startLoader());
      dispatch(searchActesAction(searchValue)); 
    } else {
      // Recharger la pagination normale
      dispatch(startLoader());
      dispatch(getActePagesAction(lazyState.page + 1, lazyState.rows));
    }
  }, 300),
  [dispatch, lazyState.page, lazyState.rows]
);

  const onGlobalFilterChange = useCallback((e) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
    debouncedSearch(value);
  }, [filters, debouncedSearch]);

  // Templates
  const formatDate = useCallback((value) => {
    return value.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const datePaiementBodyTemplate = useCallback((rowData) => {
    return formatDate(rowData.createdAt);
  }, [formatDate]);

  const dateFilterTemplate = useCallback((options) => {
    return (
      <Calendar 
        value={options.value} 
        onChange={(e) => options.filterCallback(e.value, options.index)} 
        dateFormat="dd/mm/yy" 
        placeholder="dd/mm/yyyy" 
        mask="99/99/9999" 
      />
    );
  }, []);

  const getActionsTemplate = useCallback((rowData) => {
    return (
      <div className="d-flex justify-content-center gap-1">
        {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'UPDATEACTE') && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => updateAct(e, rowData)}
            title={t("common.edit")}
          >
            <i className="fa fa-pencil"></i>
          </button>
        )}
        {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'DELETEACTE') && (
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(rowData)}
            title={t("common.delete")}
          >
            <i className="fa fa-trash"></i>
          </button>
        )}
      </div>
    );
  }, [hasMenu, canDo, t]);

  const handleDelete = useCallback((rowData) => {
    swal({
      title: t("patient.delete-act") + rowData.nameFr + " ?",
      text: t("patient.delete-message-act"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const data = { type: "ACTE", id: rowData.id };
        dispatch(startLoader());
        dispatch(deleteDataAction(data));
      }
    });
  }, [t, dispatch]);

  const setColumns = useCallback(() => [
    // { name: 'code', headerName: "Code", filterPlaceholder: t("provider.code-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null },
    { name: 'nameFr', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null },
    { name: 'familyText', headerName: t('common.family'), filterPlaceholder: t("common.family-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null },
    { name: 'garantieName', headerName: t('common.garanty'), filterPlaceholder: t("common.filter-garan"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null },
    { name: 'acteProdName', headerName: t('provider.acte-production'), filterPlaceholder: t("provider.act-prod-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null },
    { name: 'formule', headerName: t("common.formula"), style: { minWidth: '12rem' }, filter: false, bodyTemplate: null },
    { name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t("patient.created-on"), filterPlaceholder: t('facturation.create-date-filter'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate },
    { name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '8rem' }, filter: false, bodyTemplate: getActionsTemplate },
  ], [t, datePaiementBodyTemplate, dateFilterTemplate, getActionsTemplate]);

  const renderHeader = useCallback(() => {
    return (
      <div className="d-flex justify-content-between align-items-center p-2">
        <PButton 
          type="button" 
          icon="pi pi-filter-slash"  
          label={t('common.reinit')} 
          outlined 
          onClick={initFilters}
          size="small"
        />
        <div className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText 
            value={globalFilterValue} 
            onChange={onGlobalFilterChange}  
            placeholder={t('common.search')}
            size="small"
          />
        </div>
      </div>
    );
  }, [t, globalFilterValue, onGlobalFilterChange, initFilters]);

  // Update an act
  const updateAct = useCallback((e, act) => {
    e.preventDefault();
    setUpdateMode(true);
    
    setFormData({
      acteId: act.id,
      nameEn: act.nameEn || '',
      nameFr: act.nameFr || '',
      code: act.code || '',
      coefLettre: act.coefLettre || 0,
      isExclu: act.isExclu || false,
      isAccord: act.isAccord || false,
      isAmbulatoire: act.isAmbulatoire || false,
      isHospitalisation: act.isHospitalisation || false,
      isMultiple: act.isMultiple || false,
      garantieId: act.garantieId || '',
      typeActeId: act.typeActeId || '',
      lettreCleId: act.lettreCleId || '',
      providerTypeId: act.acteParTypePrestataire ? 
        act.acteParTypePrestataire.map(elt => elt.providerTypeId) : [],
      type: act.type || '',
      codeGarantie: act.codeGarantie || '',
      garantieName: act.garantieName || '',
      acteProdCode: act.acteProdCode || '',
      acteProdName: act.acteProdName || ''
    });
    
    setAddCard(true);
  }, []);

  // Effects
  useEffect(() => {
    const initializeData = async () => {
      setInitialLoading(true);
      initFilters();
      
      try {
        await Promise.all([
          dispatch(getActePagesAction(currentPage, 10)),
          dispatch(getGarantiesAction()),
          dispatch(getProvidersTypesAction())
        ]);
      } catch (error) {
        console.error('Erreur lors du chargement initial:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    initializeData();
  }, [dispatch, currentPage, initFilters]);

  useEffect(() => {
    if (search.trim().length > 0) {
      dispatch(searchActesAction(search)); 
    }
  }, [search, dispatch]);

  useEffect(() => {
    const requestedPage = lazyState.page + 1;
    const currentPageNumber = props.actesPages?.pageNumber || 1;
    const totalPages = props.actesPages?.totalPages || 1;
    
    // Seulement si la page demandée existe ET est différente
    if (requestedPage !== currentPageNumber && 
        requestedPage <= totalPages && 
        requestedPage > 0) {
      dispatch(startLoader());
      dispatch(getActePagesAction(requestedPage, lazyState.rows)); 
    }
  }, [lazyState.page, lazyState.rows, props.actesPages?.pageNumber, props.actesPages?.totalPages, dispatch]);

  useEffect(() => {
    console.log('=== DEBUG ACTES ===');
    console.log('actesPages:', props.actesPages);
    console.log('actesPages.data:', props.actesPages?.data);
    console.log('actesRes:', props.actesRes);
    console.log('globalFilterValue:', globalFilterValue);
    console.log('tableData result:', tableData());
    console.log('==================');
  }, [props.actesPages, props.actesRes, globalFilterValue]);
  // Pagination handlers
  const onGoToPage = useCallback((event) => {
    setLazyState(event);
  }, []);

  const onSortData = useCallback(() => {}, []);
  const onFilterData = useCallback(() => {}, []);

  // Table data preparation
  const tableData = useCallback(() => {
    const rawData = globalFilterValue.length > 0 && props.actesRes?.length > 0 ? 
      props.actesRes : 
      (props.actesPages?.data || []);

    return rawData.map(acte => ({
      ...acte,
      createdAt: new Date(acte.createdAt),
      formule: props.lettreCles?.find(opt => opt.id === acte.lettreCleId)?.code + ' * ' + acte.coefLettre,
      familyText: props.typeActes?.find(opt => opt.id === acte.typeActeId)?.nameFr
    }));
  }, [globalFilterValue, props.actesRes, props.actesPages, props.lettreCles, props.typeActes]);

  // Save action
  const onProviderTypeSave = useCallback((e) => {
    e.preventDefault();
    let error = false;
    const errorObj = { ...initialErrorState };
    
    if (file) {
      dispatch(startLoader());
      dispatch(importActeFileUploadAction(file));
      return;
    }
    
    if (!formData.nameFr.trim()) {
      errorObj.name = t("common.name-error");
      error = true;
    }
    
    // Note: Code field validation is optional now
    const { nameExists } = validateUniqueness();
    
    if (nameExists) {
      errorObj.name = t("common.name-already-exists") || "Ce nom existe déjà";
      error = true;
    }
    
    setErrors(errorObj);
    
    if (error) {
      swal('Oops', t('common.check-form-fields'), "error");
      return;
    }
    
    dispatch(startLoader());
    
    const payload = {
      typeActeId: formData.typeActeId,
      lettreCleId: formData.lettreCleId,
      garantieId: formData.garantieId,
      garantieName: formData.garantieName,
      nameFr: formData.nameFr,
      nameEn: formData.nameEn,
      code: formData.code,
      isExclu: formData.isExclu,
      isAccord: formData.isAccord,
      isAmbulatoire: formData.isAmbulatoire,
      isHospitalisation: formData.isHospitalisation,
      isMultiple: formData.isMultiple,
      providerTypeId: formData.providerTypeId,
      coefLettre: formData.coefLettre,
      acteProdCode: formData.acteProdCode,
      acteProdName: formData.acteProdName,
      type: formData.type
    };

    if (updateMode) {
      dispatch(updateActeAction(formData.acteId, payload));
    } else {
      dispatch(createActeAction(payload));
    }
    
    closeModal();
  }, [formData, file, updateMode, validateUniqueness, t, dispatch, closeModal, initialErrorState]);

  const selectFile = useCallback((event) => {
    setFile(event.target.files[0]);
  }, []);

  return (
    <>
      {/* Header */}
      <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
        <div>
          <h4 className="mb-1">{t("provider.acts")}</h4>
          <p className="text-muted mb-0 small">{t("common.manage-acts")}</p>
        </div>
        <div className="d-flex gap-2">
          {/* Download Template Button */}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleDownloadTemplate}
          >
            <i className="fa fa-download me-1"></i> {t("common.download-template")}
          </button>
          
          {/* Create Button */}
          {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'CREATEACTE') && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                resetForm();
                setAddCard(true);
              }}
            >
              <i className="fa fa-plus-circle me-1"></i> {t("provider.new-act")}
            </button>
          )}
        </div>
      </div>
      
      {/* Modal */}
      <Modal 
        show={addCard} 
        onHide={() => setAddCard(false)}
        size="xl"
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="h5">
            {updateMode ? t("provider.edit-act") : t("provider.create-act")}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-4">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Section 1: Informations de base */}
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.basic-info")}</h6>
              </div>
              
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.code")}</label>
                <input
                  type="text"
                  className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                  placeholder="Ex: PH001"
                  value={formData.code}
                  disabled={props.showLoading || file}
                  onChange={(e) => updateFormField('code', e.target.value)}
                />
                {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                <small className="text-muted">{t("common.code-optional")}</small>
              </div>
              
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.act-type")} *</label>
                <Select
                  value={actTypes().find(elt => elt.value === formData.type)}
                  placeholder={t("provider.choose-type")}
                  onChange={(val) => updateFormField('type', val?.value || '')}
                  isDisabled={props.showLoading || file}
                  options={actTypes()}
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              
              <div className="col-12 mb-3">
                <label className="form-label">{t("common.label")} *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  placeholder={t("provider.act-name")}
                  value={formData.nameFr}
                  disabled={props.showLoading || file}
                  required={!file}
                  onChange={(e) => {
                    updateFormField('nameFr', e.target.value);
                    updateFormField('nameEn', e.target.value);
                  }}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
            </div>

            {/* Section 2: Configuration */}
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.configuration")}</h6>
              </div>
              
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.act-family")}</label>
                <Select
                  value={props.typeActes?.filter(opt => parseInt(opt.id) === parseInt(formData.typeActeId))
                    .map(val => ({label: val.nameFr, value: val.id}))[0] || null}
                  placeholder={t("provider.choose-family")}
                  onChange={(val) => updateFormField('typeActeId', val?.value || '')}
                  isDisabled={props.showLoading || file}
                  options={props.typeActes?.map(opt => ({label: opt.nameFr, value: opt.id})) || []}
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.provider-type")}</label>
                <Select
                  value={props.providerTypes?.filter(opt => formData.providerTypeId.includes(opt.id))
                    .map(val => ({label: val.nameFr, value: val.id})) || []}
                  placeholder={t("provider.choose-type")}
                  onChange={(vals) => updateFormField('providerTypeId', vals ? vals.map(val => val.value) : [])}
                  isDisabled={props.showLoading || file}
                  options={props.providerTypes?.map(opt => ({label: opt.nameFr, value: opt.id})) || []}
                  isMulti
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.key-letter")}</label>
                <Select
                  value={props.lettreCles?.filter(opt => opt.id === formData.lettreCleId)
                    .map(val => ({label: val.code, value: val.id}))[0] || null}
                  placeholder={t("provider.choose-letter")}
                  onChange={(val) => updateFormField('lettreCleId', val?.value || '')}
                  isDisabled={props.showLoading || file}
                  options={props.lettreCles?.map(opt => ({label: opt.code, value: opt.id})) || []}
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.coefficient")}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  value={formData.coefLettre}
                  disabled={props.showLoading || file}
                  onChange={(e) => updateFormField('coefLettre', e.target.value)}
                  min="0"
                  step="0.1"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t("provider.production-act")}</label>
                <Select
                  value={props.actesProd?.filter(opt => opt.code === formData.acteProdCode)
                    .map(val => ({label: val.name, value: val.id, code: val.code, garantieId: val.garantieId}))[0] || null}
                  placeholder={t("provider.choose-act")}
                  onChange={(val) => {
                    if (val) {
                      updateFormField('acteProdCode', val.code);
                      updateFormField('acteProdName', val.label);
                      const res = props.garanties?.filter(elt => parseInt(elt.id) === val.garantieId);
                      if (res && res.length > 0) {
                        updateFormField('garantieId', res[0].code);
                        updateFormField('garantieName', res[0].libelle);
                      }
                    } else {
                      updateFormField('acteProdCode', '');
                      updateFormField('acteProdName', '');
                      updateFormField('garantieId', '');
                      updateFormField('garantieName', '');
                    }
                  }}
                  isDisabled={props.showLoading || file}
                  options={props.actesProd?.map(opt => ({
                    label: opt.name, 
                    value: opt.id, 
                    code: opt.code, 
                    garantieId: opt.garantieId
                  })) || []}
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.garanty")}</label>
                <Select
                  value={props.garanties?.filter(opt => opt.code === formData.garantieId)
                    .map(val => ({label: val.libelle, value: val.code}))[0] || null}
                  placeholder={t("provider.linked-garanty")}
                  isDisabled={true}
                  options={props.garanties?.map(opt => ({label: opt.libelle, value: opt.code})) || []}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            {/* Section 3: Options */}
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.options")}</h6>
              </div>
              
              <div className="col-12">
                <div className="row">
                  <div className="col-md-4 mb-2">
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="checkbox"
                        id="isExclu"
                        checked={formData.isExclu}
                        disabled={props.showLoading || file}
                        onChange={(e) => updateFormField('isExclu', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isExclu">
                        {t("provider.rejected")}
                      </label>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isAccord"
                        checked={formData.isAccord}
                        disabled={props.showLoading || file}
                        onChange={(e) => updateFormField('isAccord', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isAccord">
                        {t("provider.agreement")}
                      </label>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isAmbulatoire"
                        checked={formData.isAmbulatoire}
                        disabled={props.showLoading || file}
                        onChange={(e) => updateFormField('isAmbulatoire', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isAmbulatoire">
                        {t("provider.ambulatory")}
                      </label>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isHospitalisation"
                        checked={formData.isHospitalisation}
                        disabled={props.showLoading || file}
                        onChange={(e) => updateFormField('isHospitalisation', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isHospitalisation">
                        {t("provider.hospitalization")}
                      </label>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isMultiple"
                        checked={formData.isMultiple}
                        disabled={props.showLoading || file}
                        onChange={(e) => updateFormField('isMultiple', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isMultiple">
                        {t("provider.multiple")}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Import CSV */}
            <div className="row">
              <div className="col-12">
                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.import-csv")}</h6>
              </div>
              
              <div className="col-12">
                <div className="border rounded p-3">
                  <p className="text-muted mb-3 small">
                    {t("provider.import-csv-desc")}
                  </p>
                  <div className="input-group">
                    <span className="input-group-text">CSV</span>
                    <input 
                      type="file" 
                      className="form-control" 
                      onChange={selectFile}
                      accept=".csv"
                    />
                  </div>
                  {file && (
                    <div className="alert alert-info mt-2 mb-0 py-2">
                      <small>{t("common.selected-file")}: <strong>{file.name}</strong></small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <button 
            type="button" 
            className="btn btn-outline-secondary"
            onClick={closeModal}
            disabled={props.showLoading}
          >
            {t("common.cancel")}
          </button>
          
          {props.showLoading ? (
            <Button variant="primary" disabled>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="ms-2">{t("common.saving")}</span>
            </Button>
          ) : (
            <button 
              type="button"
              className="btn btn-primary"
              onClick={onProviderTypeSave}
            >
              {updateMode ? t("common.update") : t("common.save")}
            </button>
          )}
        </Modal.Footer>
      </Modal>
      
      {/* Data Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <DataTableBase 
            data={tableData()}
            emptyMessage={t("provider.no-acts")}
            filters={filters}
            globalFiltersFields={['nameFr', 'familyText', 'garantieName', 'acteProdName', 'createdAt']}
            header={renderHeader()}
            columns={setColumns()}
            exportColumns={[
              { title: 'Code', dataKey: 'code' },
              { title: t('common.name'), dataKey: 'nameFr' },
              { title: t('common.formula'), dataKey: 'formule' },
              { title: t('common.garanty'), dataKey: 'garantieName' },
              { title: t('provider.acte-production'), dataKey: 'acteProdName' },
              { title: t('provider.type'), dataKey: 'type' },
              { title: t('common.family'), dataKey: 'familyText' },
              { title: t('provider.excluded'), dataKey: 'isExclu' },
              { title: t('provider.agreement'), dataKey: 'isAccord' },
              { title: t('provider.ambulatory'), dataKey: 'isAmbulatoire' },
              { title: t('provider.hospitalization'), dataKey: 'isHospitalisation' },
              { title: t('common.created-on'), dataKey: 'createdAt' },
            ]}
            rows={10} 
            showExport={true}
            exportFilename="actes"
            loading={props.showLoading || initialLoading}
            lazy={globalFilterValue.length === 0}
            dataSet={lazyState}
            totalRecords={props.actesPages?.totalPages}
            onGoToPage={onGoToPage}
            onSortData={onSortData}
            onFilterData={onFilterData}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    actes: state.provider.actes,
    garanties: state.contrat.garanties || [],
    actesProd: state.contrat.actes || [],
    typeActes: state.provider.typeActes || [],
    actesPages: state.provider.actesPages || { data: [], totalPages: 0, totalElements: 0 },
    lettreCles: state.provider.lettreCles || [],
    actesRes: state.provider.actesRes || [],
    providerTypes: state.provider.providerTypes || [],
    errorMessage: state.provider.errorMessage || state.clientAuth.errorMessage,
    successMessage: state.provider.successMessage,
    showLoading: state.provider.showLoading || false,
    currentUser: currentUser(state) || { menus: [] }
  };
};

export default connect(mapStateToProps)(Acte);